import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { ErrorAlert, PageLoadingSpinner } from 'ui_kit_qmd'
import { ModalShell } from './components/modal-shell/ModalShell'
import { StepRenderer } from './components/step-renderer/StepRenderer'
import { Confirmations } from './components/confirmations/Confirmations'
import { type Address, type PastVisit, type Pharmacy, type Visit } from '../../types/types'
import { ProductListener } from 'store/products/ProductListener'
import { getVisitInfoForProduct, orderProduct } from 'store/products/productsThunk'
import { type Product, type ProductList, type Range } from '../../types/products/productTypes'
import { isNoteValid } from './utils'
import { useGetOrderNameFromProduct } from './hooks/useGetOrderNameFromProduct'
import { appInsights } from '../../utils/appInsights'
import errorIcon from '../../images/common/error-icon.svg'
import { getPatientTreatLinkEndpoint } from '../../store/charts/ChartsThunk'
import { useProductOrderContext, ProductOrderProvider } from './context'
import { type OrderCategory, Step } from './common'
import { apiCall } from 'utils/restclient'
import './productOrderPopUp.scss'

interface Props {
  address?: Address
  patientId: string
  tabId: string
  pharmacy: Pharmacy | undefined
  visitId?: string
  getOrders: () => void
  onVisitSelected?: (visitId: string) => Promise<void> | void
}

const emptyAddress = {
  StreetAddress1: '',
  City: '',
  StateCode: '',
  Zip: '',
  CountryCode: 'US',
}

const PopUp = ({
  address = emptyAddress,
  pharmacy,
  visitId = '',
  patientId,
  tabId,
  getOrders,
  onVisitSelected,
}: Props) => {
  const dispatch = useDispatch()

  const patientTreatLink = useRef<string>()
  const createProductErrorMsg = useRef<string>('')
  const selectedVisitId = useRef<string>(visitId)
  const selectedVisitAvailableCreditAmount = useRef<number>(0)
  const {
    cartItems,
    category,
    note,
    orderInfo,
    selectedOption,
    setSelectedOption,
    setNote,
    setOrderInfo,
    productList,
    product,
    loading,
    isShowing,
    error,
    setProductList,
    goToStep,
    goForward,
    setProduct,
    setLoading,
    setIsShowing,
    setShowOrderFailed,
    setShowOrderCreated,
    setShowOrderCreatedTreat,
    setError,
    setNoteError,
    goToEdit,
    setShowDiscardOrdersWarning,
    isMoudHomeDelivery,
    isMoudPharmacyPickup,
    isPharmacyPickup,
    setVisitList,
    deliveryType,
    openProductsAction,
    setCartItems,
  } = useProductOrderContext()

  // if the order address has already been set, use that. otherwise, use the pharmacy address if it's a moud pharmacy
  // pickup, or the address from the props
  const orderAddress = orderInfo?.Address || (isMoudPharmacyPickup ? pharmacy?.Address || emptyAddress : address)

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError('')
      }, 3500)
    }
  }, [error, setError])

  const onSelectProductHandler = useCallback(
    (selectedProduct: Product) => {
      setProduct(selectedProduct)
      goForward()
    },
    [setProduct, goForward],
  )

  const onSelectVisit = useCallback(
    async (visit: PastVisit) => {
      if (onVisitSelected) {
        setLoading(true)
        try {
          await onVisitSelected(visit.VisitId)
        } finally {
          setLoading(false)
        }
        setIsShowing(false)
        return
      }
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(getVisitInfoForProduct(visit.VisitId))
      selectedVisitId.current = visit.VisitId
    },
    [dispatch, onVisitSelected, setIsShowing, setLoading],
  )

  const setSelectedVisit = useCallback(
    (visit: Visit) => {
      selectedVisitAvailableCreditAmount.current = visit.AvailableCreditAmount

      goForward(Step.VIEW_VISIT_LIST)
    },
    [goForward],
  )

  const { getOrderNameFromProduct } = useGetOrderNameFromProduct({ isMoudHomeDelivery })

  const onConfirmDoseHandler = useCallback(() => {
    setNoteError('')
    goForward()
  }, [goForward, setNoteError])

  const onConfirmSelectionHandler = useCallback(() => {
    setNote('')
    setNoteError('')
    goForward()
  }, [setNote, goForward, setNoteError])

  const onChangeHandler = useCallback(() => {
    // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
    const option = product.Options.find((o) => o.OptionId === selectedOption.OptionId)
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    setSelectedOption({
      ...selectedOption,
      // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
      Price: option.Price,
    })
    setCartItems([])
    goToEdit()
  }, [product?.Options, setSelectedOption, selectedOption, setCartItems, goToEdit])

  const onAddOrderHandler = useCallback(
    (nextOrderAddress: Address, pharmacyId: string) => {
      if (!isNoteValid(note || '', setError, setNoteError)) return

      const isBulkOrder = cartItems.length
      setOrderInfo({
        ...orderInfo,
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        Order: {
          ...selectedOption,
          Name: getOrderNameFromProduct(product),
        },
        Address: nextOrderAddress,
        PharmacyNcpdpId: isPharmacyPickup ? pharmacyId : undefined,
        ProductId: !isBulkOrder && product ? product.ProductId : undefined,
        Items: isBulkOrder ? [...cartItems] : undefined,
      })

      goToStep(Step.VIEW_PAYMENT_ORDER)
    },
    [
      cartItems,
      note,
      setOrderInfo,
      orderInfo,
      selectedOption,
      product,
      goToStep,
      isPharmacyPickup,
      setError,
      setNoteError,
      getOrderNameFromProduct,
    ],
  )

  const openProducts = useCallback(
    (value: boolean, categoryArg?: OrderCategory, list?: ProductList[], range?: Range) => {
      // @ts-expect-error TS2379 -- MIGRATION: not approved, fix when encountered
      openProductsAction({ value, categoryArg, list, range, pharmacyFromProps: pharmacy })
    },
    [openProductsAction, pharmacy],
  )

  const openVisitsList = useCallback(
    (list: PastVisit[]) => {
      setVisitList(list)
      setIsShowing(true)
    },
    [setIsShowing, setVisitList],
  )

  const onClose = useCallback(() => {
    if (cartItems.length) {
      setShowDiscardOrdersWarning('close')
      return
    }
    // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
    setProduct(null)
    setIsShowing(false)
  }, [cartItems.length, setIsShowing, setProduct, setShowDiscardOrdersWarning])

  const onPlaceOrder = useCallback(() => {
    try {
      dispatch(
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        orderProduct(patientId, selectedVisitId.current, tabId, {
          ...orderInfo,
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          OptionId: selectedOption.OptionId,
          AccountCreditAmount: selectedVisitAvailableCreditAmount.current,
          Note: note,
          VisitCreditAmount: 0,
          Order: undefined,
          DeliveryType: deliveryType,
        }),
      )
    } catch (e) {
      setError('Error processing order.')
    }
    appInsights.trackEvent({
      name: 'Order Placed',
      properties: {
        visitId,
        patientId,
        // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
        order: orderInfo.Order,
        // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
        productId: orderInfo.ProductId,
        accountCreditAmount: selectedVisitAvailableCreditAmount.current,
      },
    })
  }, [orderInfo, visitId, patientId, dispatch, tabId, selectedOption?.OptionId, note, deliveryType, setError])

  useEffect(() => {
    setNoteError('')
  }, [note, setNoteError])

  const showResult = useCallback(
    (result: boolean | string) => {
      getOrders()
      createProductErrorMsg.current = ''
      if (result) {
        appInsights.trackEvent({
          name: 'Order Created',
          properties: {
            visitId,
            patientId,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            order: orderInfo.Order,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            productId: orderInfo.ProductId,
            accountCreditAmount: selectedVisitAvailableCreditAmount.current,
          },
        })
        switch (result) {
          case 'order-treat-success':
          case 'payment-treat-success': {
            setShowOrderCreatedTreat(true)
            break
          }
          case 'order-error':
          case 'payment-treat-error': {
            createProductErrorMsg.current = `Couldn't contact TREAT. Please log in and manually set up the order.`
            setShowOrderCreatedTreat(true)
            break
          }
          case 'order-success':
          case 'payment-success': {
            setShowOrderCreated(true)
            break
          }
          default: {
            return
          }
        }
      } else {
        appInsights.trackEvent({
          name: 'Order Failed',
          properties: {
            visitId,
            patientId,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            order: orderInfo.Order,
            // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
            productId: orderInfo.ProductId,
            accountCreditAmount: selectedVisitAvailableCreditAmount.current,
          },
        })
        setShowOrderFailed(true)
      }
      setIsShowing(false)
    },
    [
      getOrders,
      patientId,
      visitId,
      orderInfo,
      setShowOrderFailed,
      setIsShowing,
      setShowOrderCreated,
      setShowOrderCreatedTreat,
    ],
  )

  useEffect(() => {
    // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
    if (!productList.length || !selectedOption || !product) return
    // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
    const allProducts = productList.flatMap((group) => group.Products)

    const matchedProduct = allProducts.find((p) => p.ProductId === product.ProductId)

    if (!matchedProduct) {
      openProductsAction({ value: true })
      return
    }

    const newOption = matchedProduct.Options.find((o) => o.OptionId === selectedOption.OptionId)
    if (!newOption) {
      openProductsAction({ value: true })
      return
    }

    if (newOption.Price !== selectedOption.Price) {
      setSelectedOption({
        ...selectedOption,
        Price: newOption.Price,
      })
    }
  }, [productList, product, selectedOption, setSelectedOption, openProducts])

  const orderName = getOrderNameFromProduct(product)

  // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
  const productScreensTitle = orderName ? `${orderName}${category === 'moud' ? '' : ` - ${product.Duration}`}` : null

  const getTreatLink = useCallback(async (): Promise<string> => {
    if (patientTreatLink.current) return patientTreatLink.current
    const result = await apiCall(getPatientTreatLinkEndpoint(patientId), 'GET')
    // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
    return result.ProfileUrl
  }, [patientId])

  return (
    <div>
      {loading ? <PageLoadingSpinner /> : null}
      {error ? <div className='product-order-popup-error-container'>
          <ErrorAlert errorText={error} errorImg={errorIcon} imgAlt='Error' />
        </div> : null}
      <Confirmations treatErrorMessage={createProductErrorMsg.current} getTreatLink={getTreatLink} />

      <ProductListener
        patientId={patientId}
        tabId={tabId}
        setError={setError}
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        openProducts={openProducts}
        setProductList={setProductList}
        showResult={showResult}
        setLoading={setLoading}
        openVisitsList={openVisitsList}
        setSelectedVisit={setSelectedVisit}
        setTreatLink={(link: string) => {
          patientTreatLink.current = link
        }}
        isShowing={isShowing}
      />

      <ModalShell isOpen={isShowing} onClose={onClose}>
        <StepRenderer
          title={productScreensTitle}
          tabId={tabId}
          orderAddress={orderAddress}
          accountCredit={selectedVisitAvailableCreditAmount.current}
          onSelectVisit={onSelectVisit}
          onSelectProduct={onSelectProductHandler}
          onConfirmSelection={onConfirmSelectionHandler}
          onConfirmDose={onConfirmDoseHandler}
          onAddOrder={onAddOrderHandler}
          onChangeSelection={onChangeHandler}
          onPlaceOrder={onPlaceOrder}
        />
      </ModalShell>
    </div>
  )
}

interface ProviderProps extends Props {
  isRefillAllowed?: boolean
}

export const ProductOrderPopUp = ({ ...props }: ProviderProps) => (
  <ProductOrderProvider
    isPatientRefillAllowed={props.isRefillAllowed}
    patientId={props.patientId}
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    visitId={props.visitId}>
    <PopUp {...props} />
  </ProductOrderProvider>
)
